import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { authApi } from '@/utilities/axios';
import { useAuthStore } from '@/modules/auth/store';
import { showToast } from '@/utilities/toast-handler';
import { getAllIncidentCategories } from '../../incident/constants';
import { flattenArray, formatVueTreeSelectData } from '../../../../../utilities/json-formatter';

const INCIDENT_URL = process.env.VUE_APP_API_VERSION + '/incident';
const INCIDENT_TYPE_URL = process.env.VUE_APP_API_VERSION + '/incident-types';

export const useIncidentStore = defineStore('incident', () => {
    const authStore = useAuthStore();
    const company_id = computed(() => {
        return authStore.getLoggedUserCompanyId;
    });

    const incident_categories = getAllIncidentCategories();

    // state
    const incident = ref({
        incident_number: null,
        officer_in_charge: null,
        category: null,
        category_txt: null,
        incident_date: null,
        station: null,
        location: null,
        details: null,
        incident_type_id: null,
        incident_type: null,
        property_type_id: null,
        property_type: null,
        company_id: null,
        summary: null,
        incident_category: null,
        type: null,
    });

    const incidents = ref([]);

    const incidentTypes = ref([]);

    const activePanelIncidents = ref([]); // Specifically for IncidentsSelectionDialog panel

    // getters
    const incidentTypesVueTree = computed(() => {
        return formatVueTreeSelectData(incidentTypes.value);
    });

    const flatIncidentTypes = computed(() => {
        return flattenArray(incidentTypes.value);
    });

    // actions
    const fetchIncidentList = async (incidentNo = null) => {
        try {
            const response = await authApi.get(INCIDENT_URL, {
                params: {
                    incident_number: incidentNo ?? incident.value.incident_number,
                    company_id: company_id.value,
                    paginate: false,
                },
            });
            if (response.statusCode === 200) {
                incidents.value = response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch incident');
        }
    };

    const createIncident = async () => {
        try {
            const bodyParams = { ...incident.value, company_id: company_id.value };
            const response = await authApi.post(INCIDENT_URL, bodyParams);
            if (response.statusCode === 201) {
                return response.data.id;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to create incident');
        }
    };

    const fetchIncidentTypes = async () => {
        try {
            const response = await authApi.get(INCIDENT_TYPE_URL);
            if (response.statusCode === 200) {
                incidentTypes.value = response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch incident types');
        }
    };

    //helpers
    const initIncidentForm = () => {
        incident.value.incident_number = null;
        incident.value.company_id = null;
        incident.value.category = null;
        incident.value.incident_type_id = null;
        incident.value.incident_date = null;
        incident.value.property_type_id = null;
        incident.value.officer_in_charge = null;
        incident.value.category_txt = null;
        incident.value.station = null;
        incident.value.location = null;
        incident.value.details = null;
        incident.value.property_type = null;
        incident.value.incident_type = null;
        incident.value.summary = null;
        incident.value.incident_category = null;
        incident.value.type = null;
    };

    const loadIncidentDetails = (incidentData) => {
        incident.value.incident_number = parseInt(incidentData?.incident_number);
        incident.value.incident_date = incidentData?.incident_date;
        incident.value.category = parseInt(incidentData?.category);
        incident.value.property_type_id = incidentData?.property_type_id;
        incident.value.officer_in_charge = incidentData?.officer_in_charge;
        incident.value.station = incidentData?.station;
        incident.value.location = incidentData?.location;
        incident.value.details = incidentData?.details;
        incident.value.incident_type_id = incidentData?.incident_type_id;
        incident.value.incident_type = incidentData?.incident_type;
        incident.value.property_type = incidentData?.property_type;
        incident.value.summary = incidentData?.summary;
        incident.value.incident_category = incident_categories.find((incident_category) => incident_category.id == incidentData.category)?.name;
        incident.value.type = incidentData?.type?.name;
    };

    return {
        //state
        incident,
        incidents,
        incidentTypes,
        activePanelIncidents,

        //getter
        incidentTypesVueTree,
        flatIncidentTypes,
        //actions
        fetchIncidentList,
        createIncident,
        loadIncidentDetails,
        fetchIncidentTypes,

        //helpers
        initIncidentForm,
    };
});
